import { Box, Typography } from "@mui/material";
import { lighten } from "@mui/material/styles";
import { useCurrency } from "../../../context/CurrencyContext";
import { useTheme } from "../../../context/ThemeContext";
import { TonalBadge } from "../TonalBadge";
import { sharedScale } from "./sharedScale";
import { signedBar, Sx, TABULAR_NUMERALS } from "./shared";

/* =======================================================================
   Comparison — two bars on a scale shared within the pair
   ======================================================================= */

/** The width of the column that names the two bars, in theme.spacing units. */
const LABEL_WIDTH = 12;

/** The height of a bar, in px. */
const BAR_HEIGHT = 14;

/**
 * How far the bars of a level are lightened from the primary colour: the
 * actual one is the stronger, the counterfactual stands back behind it.
 */
const LEVEL_LIGHTEN = 0.15;
const QUIET_LEVEL_LIGHTEN = 0.6;

const Bar = ({
  value,
  start,
  pct,
  zeroPct,
  level,
  quiet,
}: {
  value: number;
  // where the bar begins and how wide it is, in % of the track
  start: number;
  pct: number;
  // the zero line, when the pair is drawn around it
  zeroPct?: number;
  level: boolean;
  quiet: boolean;
}) => {
  const { theme } = useTheme();

  return (
    <Box
      sx={{
        position: "relative",
        height: BAR_HEIGHT,
        bgcolor: "action.hover",
        borderRadius: 0.75,
      }}
    >
      {zeroPct !== undefined && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${zeroPct}%`,
            borderLeft: `1px solid ${theme.palette.text.disabled}`,
          }}
        />
      )}
      {/* a value of nothing has no bar: its outline alone would be one */}
      {pct > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${start}%`,
            width: `${pct}%`,
            borderRadius: 0.75,
            // the outline of a result lies within the width of its bar
            boxSizing: "border-box",
            ...(level
              ? {
                  bgcolor: lighten(
                    theme.palette.primary.main,
                    quiet ? QUIET_LEVEL_LIGHTEN : LEVEL_LIGHTEN,
                  ),
                }
              : signedBar(theme, value, quiet)),
          }}
        />
      )}
    </Box>
  );
};

interface ComparisonProps {
  title: string;
  /** What actually happened. */
  actual: number;
  actualLabel: string;
  /** The counterfactual. */
  counterfactual: number;
  counterfactualLabel: string;
  /** True when the gap favours the actual outcome. */
  favourable: boolean;
  /**
   * The whole difference line, given the formatted gap — e.g.
   * `${amount} of downside avoided`. The caller owns the sentence so the word
   * order is not fixed here.
   */
  differenceText: (amount: string) => string;
  /**
   * The two values are levels, a revenue say, with no good or bad side of
   * zero: their bars take the primary colour. Otherwise they are results, and
   * each bar takes the colour of its sign, a gain or a loss.
   */
  level?: boolean;
  sx?: Sx;
}

/**
 * What happened against what would have: the two as bars and as figures, and
 * what lies between them. The bars say what they measure, the stronger one
 * for what happened; which of the two is the better is said by the difference
 * alone.
 */
export const Comparison = ({
  title,
  actual,
  actualLabel,
  counterfactual,
  counterfactualLabel,
  favourable,
  differenceText,
  level = false,
  sx,
}: ComparisonProps) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();

  // The scale is shared within the pair only: two pairs measuring different
  // things must not be read against each other.
  const { placement } = sharedScale([actual, counterfactual]);
  const difference = Math.abs(counterfactual - actual);

  const figure = { textAlign: "right", ...TABULAR_NUMERALS } as const;

  return (
    <Box sx={sx}>
      <Typography variant="subtitle2" component="h4" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `${theme.spacing(LABEL_WIDTH)} minmax(0, 1fr) auto`,
          columnGap: 1.5,
          rowGap: 1,
          alignItems: "center",
          mt: 1.25,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {actualLabel}
        </Typography>
        <Bar
          value={actual}
          {...placement(actual)}
          level={level}
          quiet={false}
        />
        <Typography variant="body2" noWrap sx={{ ...figure, fontWeight: 700 }}>
          {currency.format(actual)}
        </Typography>

        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {counterfactualLabel}
        </Typography>
        <Bar
          value={counterfactual}
          {...placement(counterfactual)}
          level={level}
          quiet
        />
        <Typography variant="body2" noWrap sx={figure}>
          {currency.format(counterfactual)}
        </Typography>

        {/* under the bars and the figures, not under what names them */}
        <Box sx={{ gridColumn: "2 / -1" }}>
          <TonalBadge
            tone={
              favourable
                ? theme.palette.data.positive
                : theme.palette.data.negative
            }
            // a difference is a clause: it wraps where the pair is narrow
            sx={{ whiteSpace: "normal", ...TABULAR_NUMERALS }}
          >
            {differenceText(currency.format(difference))}
          </TonalBadge>
        </Box>
      </Box>
    </Box>
  );
};
